import {Inject, Injectable} from '@angular/core';
import {Observable, ReplaySubject} from 'rxjs';
import {Uuid} from '../interface/response/uuid';
import {HttpClient} from '@angular/common/http';
import {first, take} from 'rxjs/operators';
import {RegistrationIdentifier} from '../interface/registration-identifier';
import {CountedAndPaginated} from '../interface/response/counted-and-paginated';
import {ListResponse} from '../interface/response/list-response';
import {FilterService} from './filter.service';
import {DOCUMENT} from '@angular/common';
import {RegistrationIdImport} from '../interface/registration-id-import';
import {ReportCustom} from "../interface/report-custom";

@Injectable({
  providedIn: 'root'
})
export class IdentifierService {
  public readonly listSubject: ReplaySubject<CountedAndPaginated<RegistrationIdentifier>>
    = new ReplaySubject<CountedAndPaginated<RegistrationIdentifier>>(1);
  public readonly notCountedListSubject: ReplaySubject<ListResponse<RegistrationIdentifier>>
    = new ReplaySubject<ListResponse<RegistrationIdentifier>>(1);
  public readonly importListSubject: ReplaySubject<CountedAndPaginated<RegistrationIdImport>>
    = new ReplaySubject<CountedAndPaginated<RegistrationIdImport>>(1);

  constructor(
    private httpClient: HttpClient,
    private filterService: FilterService,
    @Inject(DOCUMENT) private document: Document,
  ) { }

  public sendForm(formData: object, identifierId?: string): Observable<any> {
    if (identifierId) {
      return this.httpClient.put(`/rest/crm/registration-id/${identifierId}`, formData)
        .pipe(
          take(1)
        );
    } else {
      return this.httpClient.post('/rest/crm/registration-id', formData)
        .pipe(
          take(1)
        );
    }
  }

  public get(uuid: string): Observable<RegistrationIdentifier> {
    return this.httpClient.get(`/rest/crm/registration-id/${uuid}`)
      .pipe(first()) as Observable<RegistrationIdentifier>;
  }

  public deleteIdentifier(uuid: string): Observable<any> {
    return this.httpClient.delete(`/rest/crm/registration-id/${uuid}`)
      .pipe(first());
  }

  public loadList(page: number, pageSize: number, filters: object): void {
    this.httpClient.get(`/rest/crm/registration-id?page=${page + 1}&pageSize=${pageSize}&`
      + this.filterService.parseFilters(filters))
      .pipe(
        first()
      )
      .subscribe((result: CountedAndPaginated<RegistrationIdentifier>) => {
        this.listSubject.next(result);
      });
  }

  public loadImportList(page: number, pageSize: number, filters: object): void {
    this.httpClient.get(`/rest/crm/registration-id-import?page=${page + 1}&pageSize=${pageSize}&`
      + this.filterService.parseFilters(filters))
      .pipe(
        first()
      )
      .subscribe((result: CountedAndPaginated<RegistrationIdImport>) => {
        this.importListSubject.next(result);
      });
  }

  public loadNotCountedList(
    page: number,
    pageSize: number,
    filters: object,
    observable?: ReplaySubject<ListResponse<RegistrationIdentifier>>
  ): void {
    this.httpClient
      .get(`/rest/crm/registration-id?disableCount=1&page=${page + 1}&pageSize=${pageSize}&`
        + this.filterService.parseFilters(filters))
      .pipe(take(1))
      .subscribe((result: ListResponse<RegistrationIdentifier>) => {
        if (observable) {
          observable.next(result);
        } else {
          this.notCountedListSubject.next(result);
        }
      });
  }

  public downloadReport(filters: any): Observable<Uuid> {
    return this.httpClient.post(`/rest/crm/registration-id/method/report?`
      + this.filterService.parseFilters(filters), {})
      .pipe(first()) as Observable<Uuid>;
  }

  public downloadImportsReport(filters: any): Observable<Uuid> {
    return this.httpClient.post(`/rest/crm/registration-id-import/method/report?`
      + this.filterService.parseFilters(filters), {})
      .pipe(first()) as Observable<Uuid>;
  }

  public downloadImportFile(importId: string): void {
    this.document.location.href = `/rest/crm/registration-id-import/${importId}/downloadFile`;
  }

  public downloadImportBackup(importId: string): void {
    this.document.location.href = `/rest/crm/registration-id-import/${importId}/downloadBackup`;
  }

  public import(formData: object): Observable<Uuid> {
    return this.httpClient.post(`/rest/crm/registration-id/method/import`, formData)
      .pipe(
        take(1)
      ) as Observable<Uuid>;
  }

  public getImport(importId: string): Observable<RegistrationIdImport> {
    return this.httpClient.get(`/rest/crm/registration-id-import/${importId}`)
      .pipe(first()) as Observable<RegistrationIdImport>;
  }

  public downloadCustomReport(filters: object): Observable<Uuid> {
    return this.httpClient.post(`/rest/crm/registration-id/custom/method/report?` + this.filterService.parseFilters(filters), {})
      .pipe(first()) as Observable<Uuid>;
  }
  public getCustomReportSettings(reportName: string): Observable<ReportCustom> {
    return this.httpClient.get(`/rest/crm/report/custom/settings/${reportName}`)
      .pipe(first()) as Observable<ReportCustom>
      ;
  }
  public sendCustomReportSettings(formData: object): Observable<any> {
    return this.httpClient.post('/rest/crm/report/custom/settings', formData)
      .pipe(take(1));
  }

  public downloadImportsValueReport(registrationIdImport: string):  Observable<any>  {
    return this.httpClient.get( `/rest/crm/registration-id-value/send/${registrationIdImport}/logins`, {})
  .pipe(first()) as Observable<Uuid>;
  }

  public downloadCustomReportRegional(filters: object): Observable<Uuid> {
    return this.httpClient.post(`/rest/crm/registration-id/regional/custom/method/report?` + this.filterService.parseFilters(filters), {})
      .pipe(first()) as Observable<Uuid>;
  }
}
